import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { type ManualScope } from '@jupi/shared'
import { RemoteContent, Title, formatDate } from '@jupi/ui'
import { useManuals } from '../../api/school'

const API_BASE_URL = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? ''

// Manuals this member may read, grouped by who they are for. Used twice:
// the Flight school area lists its departments' manuals, and the Club
// area lists the generic ones meant for every member. Uploading is in the
// admin app.
export default function ManualsPage({ scopes, titleKey }: { scopes: ManualScope[]; titleKey: string }) {
  const { t } = useTranslation()
  const { data: all = [], isLoading, error } = useManuals()
  const manuals = all.filter((m) => scopes.includes(m.scope))

  return (
    <Box>
      <Title label={t(titleKey)} />
      <RemoteContent isLoading={isLoading} error={error}>
        {manuals.length === 0 && !isLoading ? (
          <Typography color="text.secondary">{t('school.noManuals')}</Typography>
        ) : null}
        {scopes.map((scope) => {
          const own = manuals.filter((m) => m.scope === scope)
          if (own.length === 0) return null
          return (
            <Box key={scope} sx={{ mb: 3 }}>
              {scopes.length > 1 ? (
                <Typography variant="overline" color="text.secondary">
                  {t(`school.scope_${scope}`)}
                </Typography>
              ) : null}
              <Paper variant="outlined">
                <List disablePadding>
                  {own.map((manual) => (
                    <ListItemButton
                      key={manual.manualId}
                      component="a"
                      href={`${API_BASE_URL}/api/school/manuals/${manual.manualId}/download`}
                      divider
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Icon icon="mdi:file-document-outline" width={24} />
                      </ListItemIcon>
                      <ListItemText
                        primary={manual.title}
                        secondary={[
                          manual.version ? t('school.version', { version: manual.version }) : null,
                          manual.revisionDate ? t('school.revised', { date: formatDate(`${manual.revisionDate}T12:00:00Z`) }) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                      <Icon icon="mdi:download" width={20} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Box>
          )
        })}
      </RemoteContent>
    </Box>
  )
}
